import React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import JSZip from 'jszip';
import Button, {ButtonLink} from 'sail/Button';
import Spinner from 'sail/Spinner';

import {useGuide} from '../data/GuideProvider';
import {useContent} from '../data/ContentProvider';
import {logError} from '../util';

const messages = defineMessages({
  couldNotDownload: {
    id: 'docsApp.integrationBuilder.code.couldNotDownload',
    description: 'Could not download label',
    defaultMessage: 'Could not download',
  },
  downloaded: {
    id: 'docsApp.integrationBuilder.code.downloaded',
    description: 'Downloaded label',
    defaultMessage: 'Downloaded',
  },
  download: {
    id: 'docsApp.integrationBuilder.code.download',
    description: 'Download label',
    defaultMessage: 'Download',
  },
  downloadProject: {
    id: 'docsApp.integrationBuilder.code.downloadProject',
    description: 'Download project label',
    defaultMessage: 'Download project',
  },
  downloadFullApp: {
    id: 'docsApp.integrationBuilder.code.downloadFullApp',
    description: 'Download full app',
    defaultMessage: 'Download full app',
  },
});

function useDownloadFiles() {
  const {files, docs, integrationType, extensions} = useContent();
  const {fireEvent} = useGuide();
  const [downloaded, setDownloaded] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [error, setError] = React.useState(false);

  let icon = 'download';
  if (error) {
    icon = 'cross';
  } else if (downloaded) {
    icon = 'check';
  }

  async function onClick() {
    const filesToDownload = {...files, ...docs};
    const zip = new JSZip();
    Object.values(filesToDownload).forEach((file) => {
      zip.file(file.name, file.raw);
    });
    const link = document.createElement('a');
    link.style = 'position: absolute; left: -1000px; top: -1000px';

    const timeout = setTimeout(setDownloading, 50, true);

    try {
      if (document.body) {
        const fileZip = await zip.generateAsync({type: 'base64'});
        link.href = `data:application/zip;base64,${fileZip}`;
        link.download = `stripe-${integrationType}-sample-code.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloaded(true);
        clearTimeout(timeout);
        setDownloading(false);

        setTimeout(() => {
          setDownloaded(false);
        }, 1500);
        fireEvent('download_files', {extensions});
      }
    } catch (e) {
      clearTimeout(timeout);
      setDownloading(false);
      setError(false);
      setTimeout(() => {
        setError(false);
      }, 1500);
      // Report to sentry
      logError(
        new Error(
          `Failed to download ${Object.keys(filesToDownload)
            .map((f) => filesToDownload[f].name)
            .join(',')} with error ${e.message}`,
        ),
      );
    }
  }

  return {
    files,
    icon,
    downloading,
    downloaded,
    error,
    onClick,
  };
}

export function DownloadButton() {
  const {icon, error, downloading, onClick} = useDownloadFiles();

  return (
    <Button
      icon={icon}
      label={
        error ? (
          <FormattedMessage {...messages.couldNotDownload} />
        ) : (
          <FormattedMessage {...messages.downloadFullApp} />
        )
      }
      pending={downloading}
      onClick={onClick}
      color="blue"
    />
  );
}

export function DownloadButtonLink() {
  const {
    files,
    icon,
    downloaded,
    downloading,
    error,
    onClick,
  } = useDownloadFiles();

  let label;
  if (error) {
    label = <FormattedMessage {...messages.couldNotDownload} />;
  } else if (downloaded) {
    label = <FormattedMessage {...messages.downloaded} />;
  } else {
    const filesLength = Object.keys(files).length;
    label =
      // Shorten label if we have more than three files in the <Code> view
      filesLength > 3 ? (
        <FormattedMessage {...messages.download} />
      ) : (
        <FormattedMessage {...messages.downloadProject} />
      );
  }

  return downloading ? (
    <Spinner />
  ) : (
    <ButtonLink
      display="inline-flex"
      icon={icon}
      iconPosition="right"
      label={label}
      size="small"
      onClick={onClick}
      style={{
        marginTop: 0,
        marginRight: 0,
        padding: 0,
      }}
    />
  );
}
